import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

interface CodeInputProps {
  fields?: number;
  value?: string;
  inputMode?: 'numeric' | 'text';
  onChange: (value: string) => void;
}

export default function CodeInput({
  fields = 6,
  value = '',
  inputMode = 'numeric',
  onChange,
}: CodeInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const isNumeric = inputMode === 'numeric';

  const chars = Array.from({ length: fields }, (_, i) => value[i] ?? '');

  const focusInput = (index: number) => {
    const target = inputsRef.current[index];
    if (target) {
      target.focus();
      target.select();
    }
  };

  const setChar = (index: number, char: string) => {
    const next = chars.slice();
    next[index] = char;
    onChange(next.join(''));
  };

  const handleChange = (index: number, raw: string) => {
    const cleaned = isNumeric ? raw.replace(/\D/g, '') : raw;
    if (!cleaned) {
      setChar(index, '');
      return;
    }
    // Take the last typed character in case the field already had a value.
    const char = cleaned.slice(-1);
    setChar(index, char);
    if (index < fields - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (chars[index]) {
        setChar(index, '');
      } else if (index > 0) {
        e.preventDefault();
        setChar(index - 1, '');
        focusInput(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < fields - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (index: number, e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const cleaned = isNumeric ? pasted.replace(/\D/g, '') : pasted;
    if (!cleaned) return;
    const next = chars.slice();
    for (let i = 0; i < cleaned.length && index + i < fields; i++) {
      next[index + i] = cleaned[i];
    }
    onChange(next.join(''));
    const nextFocus = Math.min(index + cleaned.length, fields - 1);
    focusInput(nextFocus);
  };

  return (
    <div className="react-code-input">
      {chars.map((char, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          type="text"
          inputMode={inputMode}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={char}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}
